import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import logoutManager from '../logout';
import api from '../api';
import COEHeader from './COEHeader';
import { 
  ClipboardCheck, 
  BarChart3, 
  BellRing, 
  ShieldCheck,
  FileText,
  Search,
  LayoutGrid,
  ChevronRight,
  GraduationCap
} from 'lucide-react';

const moduleCategories = [
  {
    category: "Academic Configuration",
    items: [
      { title: 'Student Promotion Manager', path: '/coe/student-promotion-manager', desc: 'Manage student promotions, view eligible students, and process promotion decisions.', icon: <GraduationCap />, color: 'purple', disabled: false },
      { title: 'Promotion Policies', path: '/coe/promotion-policies', desc: 'Define student promotion rules and criteria.', icon: <ShieldCheck />, color: 'blue' },
      { title: 'Exam Component Manager', path: '/coe/exam-component-manager', desc: 'Manage sections and question paper types.', icon: <ClipboardCheck />, color: 'pink' },
    ]
  },
  {
    category: "Examination Management",
    items: [
      { title: 'Exam Paper Template Management', path: '/coe/exam-paper-templates', desc: 'Create and manage dynamic paper structures.', icon: <LayoutGrid />, color: 'indigo' },
      { title: 'Paper & Evaluator Assignment', path: '/coe/evaluation-assignment', desc: 'Assign paper templates to subjects and map evaluators for assessment workflows.', icon: <ClipboardCheck />, color: 'blue' },
      { title: 'Finalized Template Management', path: '/coe/finalized-templates', desc: 'View, suspend, or delete finalized templates for any academic context.', icon: <ClipboardCheck />, color: 'emerald' },
      { title: 'Program & Course Outcomes', path: '/coe/program-course-outcomes', desc: 'Map COs and POs for outcome-based education.', icon: <FileText />, color: 'cyan' },
    ]
  },
  {
    category: "Students & Marks Management",
    items: [
      { title: 'Student-wise Marks Explorer', path: '/coe/student-marks', desc: 'Explore and view marks for individual students in any academic context.', icon: <BellRing />, color: 'amber' },
      { title: 'CIE Marks Dashboard', path: '/coe/dashboard', desc: 'View subject-wise CIE marks summary and detailed reports.', icon: <BarChart3 />, color: 'indigo' },
      { title: 'End Exam Marks Manager', path: '/coe/end-exam-marks', desc: 'Review evaluator raw marks, edit values, and apply COE bulk enhancements with automatic recalculation.', icon: <FileText />, color: 'cyan' },
    ]
  }
];

const COELandingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user] = useState(location.state?.user || null);
  const [tenant, setTenant] = useState(location.state?.tenant || null);
  const [orgInfo, setOrgInfo] = useState({ name: '', logoUrl: '' });
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!tenant) {
      setTenant({
        name: localStorage.getItem('tenantName') || '',
        subdomain: localStorage.getItem('tenantSubdomain') || '',
        id: localStorage.getItem('tenantId') || ''
      });
    }
  }, [tenant]);

  useEffect(() => {
    const tenantId = localStorage.getItem('tenantId') || tenant?.id || tenant?._id;
    if (tenantId) {
      api.get(`/api/organization/basic-info/${tenantId}`)
        .then(res => setOrgInfo({ name: res.data?.name || '', logoUrl: res.data?.logoUrl || '' }))
        .catch(() => setOrgInfo({ name: '', logoUrl: '' }));
    }
  }, [tenant]);

  const handleLogout = () => logoutManager.logout({ redirectTo: '/' });

  const filteredCategories = useMemo(() => {
    return moduleCategories.map(cat => ({
      ...cat,
      items: cat.items.filter(item => 
        item.title.toLowerCase().includes(search.toLowerCase()) || 
        item.desc.toLowerCase().includes(search.toLowerCase())
      )
    })).filter(cat => cat.items.length > 0);
  }, [search]);

  const handleNavigation = (item) => {
    // Navigation for Student Promotion Manager
    if (item.path === '/coe/student-promotion-manager') {
      navigate(item.path, { 
        state: { 
          user: user,
          tenant: tenant,
          orgInfo: orgInfo
        } 
      });
      return;
    }
    
    // Specialized logic for Student-wise Marks if needed
    if (item.path === '/coe/student-marks') {
      navigate(item.path, { state: { user, tenant, orgInfo, students: [], cieComponents: [] } });
      return;
    }
    
    if (item.path === '/coe/end-exam-marks') {
      navigate(item.path, { state: { user, tenant, orgInfo, context: null } });
      return;
    }
    
    // Default navigation for other modules
    navigate(item.path, { state: { user, tenant, orgInfo } });
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#F9FAFB] selection:bg-indigo-100">
      <COEHeader user={user} tenant={tenant} orgInfo={orgInfo} onLogout={handleLogout} />
      
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-12">
        
        {/* Welcome & Search Section */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-10 md:mb-16">
          <div className="space-y-1">
            <h1 className="text-2xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
              COE Command Center
            </h1>
            <p className="text-sm md:text-base text-slate-500 font-medium leading-relaxed">
              Welcome, {user?.firstName || 'Controller'}. Efficiently manage your institution's academic cycle.
            </p>
          </div>

          <div className="relative w-full lg:w-96">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input 
              type="text"
              placeholder="Search modules..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-50 focus:border-indigo-500 transition-all outline-none text-sm font-medium shadow-sm"
            />
          </div>
        </div>

        {/* Modules Grid */}
        <div className="space-y-12 md:space-y-20">
          {filteredCategories.map((cat, idx) => (
            <section key={idx} className="animate-in fade-in slide-in-from-bottom-4 duration-500" style={{ animationDelay: `${idx * 100}ms` }}>
              <div className="flex items-center gap-4 mb-6 md:mb-8">
                <h2 className="text-[11px] md:text-xs font-black uppercase tracking-[0.25em] text-slate-400 whitespace-nowrap">
                  {cat.category}
                </h2>
                <div className="h-px w-full bg-slate-200"></div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
                {cat.items.map((item, itemIdx) => (
                  <button
                    key={itemIdx}
                    onClick={() => handleNavigation(item)}
                    className={`group relative flex flex-col p-5 md:p-8 bg-white border border-slate-100 rounded-[1.5rem] md:rounded-[2rem] transition-all duration-300 text-left ${
                      item.disabled 
                        ? 'opacity-80 hover:shadow-md hover:-translate-y-0 cursor-pointer' 
                        : 'hover:shadow-2xl hover:shadow-indigo-100 hover:-translate-y-1 active:scale-[0.98]'
                    }`}
                  >
                    <div className={`w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-2xl md:rounded-[1.25rem] mb-6 transition-all group-hover:scale-110
                      ${item.color === 'blue' ? 'bg-blue-50 text-blue-600' : ''}
                      ${item.color === 'pink' ? 'bg-pink-50 text-pink-600' : ''}
                      ${item.color === 'indigo' ? 'bg-indigo-50 text-indigo-600' : ''}
                      ${item.color === 'cyan' ? 'bg-cyan-50 text-cyan-600' : ''}
                      ${item.color === 'emerald' ? 'bg-emerald-50 text-emerald-600' : ''}
                      ${item.color === 'purple' ? 'bg-purple-50 text-purple-600' : ''}
                      ${item.color === 'amber' ? 'bg-amber-50 text-amber-600' : ''}
                    `}>
                      {React.cloneElement(item.icon, { className: "w-6 h-6 md:w-7 md:h-7" })}
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="text-base md:text-xl font-bold text-slate-800 group-hover:text-indigo-600 transition-colors tracking-tight">
                        {item.title}
                      </h3>
                      <p className="text-xs md:text-sm text-slate-500 font-medium mt-2 leading-relaxed line-clamp-2 md:line-clamp-none">
                        {item.desc}
                      </p>
                      {item.disabled && (
                        <span className="inline-block mt-3 text-[10px] font-semibold text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                          Coming Soon
                        </span>
                      )}
                    </div>

                    <div className="mt-6 flex items-center justify-between w-full">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-300 group-hover:text-indigo-500 transition-colors">
                        {item.disabled ? 'Preview' : 'Access Module'}
                      </span>
                      <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-all">
                        <ChevronRight className="w-4 h-4 md:w-5 md:h-5 transition-transform group-hover:translate-x-0.5" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>

      <footer className="w-full py-8 md:py-10 bg-white border-t border-slate-100 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex flex-col items-center md:items-start gap-1">
            <p className="text-[9px] text-slate-300 font-bold uppercase tracking-widest">© 2026 Truyon Technology ERP</p>
          </div>
          
          <div className="flex gap-8">
            <button className="text-[10px] font-bold text-slate-400 uppercase tracking-widest hover:text-indigo-600 transition-colors">Guides</button>
            <button className="text-[10px] font-bold text-slate-400 uppercase tracking-widest hover:text-indigo-600 transition-colors">Help Desk</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default COELandingPage;